import { useEffect, useState, useContext } from 'react';
import PageGridItem from '../Shared/Page/PageGridItem';
import FlexWrapper from '../FlexWrapper';
import Divider from '../../components/Divider';
import LoadingIcon from '../../components/LoadingIcon';
import WorkflowInfoBlock from './WorkflowInfoBlock';
import WorkflowDataSynchronisation from './WorkflowDataSynchronisation'
import WorkflowComplianceActions from './WorkflowComplianceActions'
import WorkflowPublishActions from './WorkflowPublishActions'
import { useParams } from 'react-router-dom';
import { useRemote, useStorage } from '../../Utils/Utils';
import { BannerContext } from '../../InternalApiApp';
import { WorkflowContext } from './ActiveWorkflowsPage';
import { useWebsockets } from "../../Utils/WebsocketReceiverHook";
import './ActiveWorkflows.scss'

const TaskNames = {
	"Data Synchronisation": "Data Actions",
	"Compliance Actions": "Compliance Actions",
	"Publish Actions": "Publish Actions",
}

export const statuses = {
	CANCELLED: "Cancelled",
	SKIPPED: "Skipped",
	PENDING: "Pending",
	PENDING_FEEDBACK: "Pending Feedback",
	IN_PROGRESS: "In Progress",
	COMPLETED: "Completed",
	COMPLETE: "Complete",
	INCOMPLETE: "Incomplete",
	ERROR: "Error",
	APPROVED: "Approved",
	FAILED: "Failed",
	READY: "Ready",
	RUNNING: "Running",
	PAUSE: "Pause",
	REJECTED: "Rejected",
	ACTIVE: "Active",
	INACTIVE: "Inactive",
};


const WorkflowDetails = () => {
	const [workflow, setWorkflow] = useState(null)
	const [viewStep, setViewStep] = useState(0)
	const [loading, setLoading] = useState(true)
	const params = useParams()
	const remote = useRemote()
	const storage = useStorage()
	const sockets = useWebsockets()
	const { setBannerBreadCrumbs } = useContext(BannerContext)
	const { setPausedFiles } = useContext(WorkflowContext)

	useEffect(() => {
		storage.getOrFetch("workflows/list")
		remote.get(`/workflows/event/info/${params.workflowId}?event=Batch Upload Has On Hold`).then(fetchedEvents => {
			//console.log("#### WorkflowDetails Fetched events event=Batch Upload Has On Hold`: ", fetchedEvents);
			setPausedFiles(fetchedEvents.length);
		})

		// fetch workflow details
		remote.get(`workflows/workflow/${params.workflowId}`).then((res) => {
			setBannerBreadCrumbs({ tab: 'Workflow Monitor', param1: { name: res.processName, id: params.workflowId } })
			setWorkflow(res);
			setLoading(false);
			const taskNames = { "Data Synchronisation": 0, "Compliance Actions": 1, "Publish Actions": 2 };

			if (!res.tasks || res.tasks.length === 0) setViewStep(null);
			else {
				const orderedTaskNames = ["Data Synchronisation", "Compliance Actions", "Publish Actions"];

				for (let taskName of orderedTaskNames) {
					const task = res.tasks.find(task => {
						if (!task.status || task.taskName !== taskName) return false;
						const taskStatus = statuses[task.status];
						return taskStatus !== statuses.SKIPPED && taskStatus !== statuses.COMPLETE;
					});
					if (task) {
						setViewStep(taskNames[taskName]);
						return;
					}
				}
				setViewStep(2); // Default to "Publish Actions" if no other tasks are incomplete
			}
		})
		const socketsPath = `/api/workflows/workflow/${params.workflowId}`
		sockets.subscribe({
			[socketsPath]: [(message) => {
				storage.refresh(`workflows/workflow/${params.workflowId}`)
					.then(res => {
						setWorkflow(res);
						setLoading(false);
					})
					.catch((err) => {
						console.error(err)
						setLoading(false)
					});
			}]
		})
		return () => {
			sockets.unsubscribe([`/api/workflows/workflow/${params.workflowId}`])
		}
	}, [])

	if (!workflow) return <PageGridItem colSpan="12"><LoadingIcon centered /></PageGridItem>

	const dataSynchronisation = workflow && workflow.tasks && workflow.tasks.find(task => task.taskName === "Data Synchronisation") ? workflow.tasks.find(task => task.taskName === "Data Synchronisation") : null;
	const complianceActions = workflow && workflow.tasks && workflow.tasks.find(task => task.taskName === "Compliance Actions") ? workflow.tasks.find(task => task.taskName === "Compliance Actions") : null;
	const publishActions = workflow && workflow.tasks && workflow.tasks.find(task => task.taskName === "Publish Actions") ? workflow.tasks.find(task => task.taskName === "Publish Actions") : null;

	function getTask(tasks) {
		if (loading || viewStep === null) return null
		if (viewStep === 0) return <WorkflowDataSynchronisation workflowId={workflow.id} taskId={dataSynchronisation.id} isComplete={statuses[dataSynchronisation.status] === statuses.COMPLETE} />
		else if (viewStep === 1) return <WorkflowComplianceActions workflowId={workflow.id} taskId={complianceActions.id} isComplete={statuses[complianceActions.status] === statuses.COMPLETE} historical={!workflow.isActive} />
		else return <WorkflowPublishActions workflowId={workflow.id} taskId={publishActions.id} isComplete={statuses[publishActions.status] === statuses.COMPLETE} />
	}
	const dataStatus = dataSynchronisation && dataSynchronisation.status && dataSynchronisation.status.toUpperCase();
	const complianceStatus = complianceActions && complianceActions.status && complianceActions.status.toUpperCase();
	const publishStatus = publishActions && publishActions.status && publishActions.status.toUpperCase();

	const onClickDataAction = () => {
		if (dataStatus !== statuses.SKIPPED.toUpperCase()) setViewStep(0)
	}

	const isDataActionClickable = () => {
		return dataStatus !== statuses.SKIPPED.toUpperCase()
	}

	if (loading) return <PageGridItem colSpan="12"><LoadingIcon centered /></PageGridItem>

	return (
		<>
			<PageGridItem fullWidth style={{ maxWidth: "1440px" }}>
				<FlexWrapper style={{ height: "120px" }} gap="l" >
					<WorkflowInfoBlock
						header={<span style={{ fontWeight: 600 }}>Summary {workflow.publishDisplay}</span>}
						status={statuses[workflow.status]}
						completedTasks={workflow?.progressCurrent ? workflow.progressCurrent : 0}
						totalTasks={workflow?.progressTotal ? workflow.progressTotal : 1}
						actionName="Completed"
						workflowSummary
						publishDate={workflow.publishDate}
						timeFrame={workflow.timeFrame}
					/>
				</FlexWrapper >
				<Divider noMargin marginTop="0.5rem" />
			</PageGridItem>
			<PageGridItem fullWidth showScrollbar style={{ maxWidth: "1440px" }}>
				<FlexWrapper style={{ height: "120px" }} gap="l" >
					{dataSynchronisation &&
						<>
							<FlexWrapper direction="column" style={{ gap: "1rem" }}>
								<WorkflowInfoBlock
									header={TaskNames[dataSynchronisation.taskName]}
									status={dataSynchronisation.status}
									completedTasks={dataSynchronisation.progressCurrent}
									totalTasks={dataSynchronisation.progressTotal}
									actionName="Coverage"
									onClick={() => onClickDataAction()}
									clickable={isDataActionClickable()}
									active={complianceStatus === statuses.PENDING.toUpperCase()}
									isSelected={viewStep === 0}
								/>
							</FlexWrapper>
							<Divider height={94} vertical />
						</>
					}
					{complianceActions &&
						<>
							<FlexWrapper direction="column" style={{ gap: "1rem" }}>
								<WorkflowInfoBlock
									header={TaskNames[complianceActions.taskName]}
									status={complianceActions.status}
									completedTasks={complianceActions.progressCurrent}
									totalTasks={complianceActions.progressTotal}
									actionName="Approved"
									onClick={() => complianceStatus !== statuses.PENDING.toUpperCase() && setViewStep(1)}
									clickable={complianceStatus !== statuses.PENDING.toUpperCase() ? true : false}
									active={complianceStatus !== statuses.PENDING.toUpperCase() && publishStatus === statuses.PENDING.toUpperCase()}
									isSelected={viewStep === 1}
								/>
							</FlexWrapper>
							<Divider height={94} vertical />
						</>
					}
					{publishActions &&
						<>
							<FlexWrapper direction="column" style={{ gap: "1rem" }}>
								<WorkflowInfoBlock
									header={TaskNames[publishActions.taskName]}
									status={publishActions.status}
									completedTasks={publishActions.progressCurrent}
									totalTasks={publishActions.progressTotal}
									actionName="Actions Completed"
									onClick={() => publishStatus !== statuses.PENDING.toUpperCase() && setViewStep(2)}
									clickable={publishStatus !== statuses.PENDING.toUpperCase() ? true : false}
									active={publishStatus !== statuses.PENDING.toUpperCase() && publishStatus !== statuses.COMPLETE.toUpperCase()}
									isSelected={viewStep === 2}
								/>
							</FlexWrapper>
						</>
					}
				</FlexWrapper>
				<Divider spacingTop="m" />
			</PageGridItem>
			<PageGridItem fullWidth style={{ overflowY: "auto", height: "calc(100vh - 400px)" }} >
				{workflow && workflow.tasks && getTask(workflow?.tasks)}
			</PageGridItem>
		</>
	);
}

export default WorkflowDetails;